import { createContext, useContext, type ReactNode } from 'react'

/**
 * The signed-in session, published to the routed surfaces.
 *
 * Beside `App.tsx` rather than in `src/routes/`, where super-ui keeps its
 * copy. That directory is the file-route tree here — `@tanstack/router-plugin`
 * turns every file in it into a route — so a `routes/session.tsx` would mount
 * this context at `/session`. super-ui hand-builds its router and has no such
 * constraint.
 */

/**
 * What the routed surfaces need from the signed-in session.
 *
 * Deliberately React context rather than TanStack Router context: the router
 * is built once per session, and router context is captured at construction,
 * so a value that changes while the session lives — an error, say — would
 * never reach a route. Context re-renders; router options do not.
 */
export interface Session {
  userName: string
  error: string | null
  onSignOut: () => void
}

const SessionContext = createContext<Session | null>(null)

/**
 * SessionProvider — publishes the signed-in session to the routed surfaces.
 *
 * @param props - Provider props.
 * @param props.value - The session to publish.
 * @param props.children - The signed-in tree.
 * @returns The provider.
 */
export function SessionProvider({
  value,
  children
}: {
  value: Session
  children: ReactNode
}): ReactNode {
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

/**
 * Read the signed-in session.
 *
 * Throws rather than returning null: every consumer is inside the signed-in
 * tree by construction, so a missing provider is a wiring bug worth failing
 * loudly instead of a null check at each call site.
 *
 * @returns The session.
 */
export function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) {
    throw new Error('useSession used outside a SessionProvider')
  }

  return session
}
